import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import { discoverKvUri } from "../../lib/brain-paths.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  planAgentRemoval,
  executeAgentRemoval,
  defaultRemoveAgentDeps,
  type RemoveOpts,
} from "../../lib/agent-remove.js";

export class AgentRemoveCommand extends M8tCommand {
  static paths = [["agent", "remove"]];
  static usage = Command.Usage({
    description: "Cascade-remove an agent and all its associated resources.",
    details:
      "Removes, in order: channel bindings (table row + KV secret + conversations), a2a connection, brain connection (GitHub repo kept by default), the agent itself, and its local yaml. Idempotent and partial-failure resilient.",
    examples: [
      ["Remove an agent non-interactively", "$0 agent remove my-agent --yes"],
      ["Remove and also delete the brain repo", "$0 agent remove my-agent --yes --delete-brain-repo"],
      ["Remove without touching bindings", "$0 agent remove my-agent --yes --keep-bindings"],
    ],
  });

  name = Option.String();
  yes = Option.Boolean("--yes", false);
  keepBindings = Option.Boolean("--keep-bindings", false);
  keepA2a = Option.Boolean("--keep-a2a", false);
  deleteBrainRepo = Option.Boolean("--delete-brain-repo", false);
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  kvUri = Option.String("--kv-uri");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize clipanion descriptor objects that appear when flags are unset in tests.
    const name = typeof this.name === "string" ? this.name : undefined;
    if (!name) {
      throw new LocalCliError({ code: "USAGE", message: "<name> positional argument is required" });
    }

    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const progress =
      mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const isTTY = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    // clipanion Option.Boolean fields hold a truthy descriptor until argv parse;
    // `=== true` distinguishes an explicitly-passed flag from that descriptor
    // (a bare truthy check would flip these defaults under unit-test construction).
    const yes = this.yes === true;
    const keepBindings = this.keepBindings === true;
    const keepA2a = this.keepA2a === true;
    const deleteBrainRepo = this.deleteBrainRepo === true;

    const env = this.context.env;
    const kvUriOverride = typeof this.kvUri === "string" ? this.kvUri : undefined;
    // resolveKvUri is a thunk — only called when unlinkBrain actually runs,
    // so agents with no brain never trigger KV_URI_NOT_FOUND at startup.
    const resolveKvUri = () => discoverKvUri(env, kvUriOverride);

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;

    // Use per-agent yaml to narrow project discovery (skip interactive for known endpoints).
    const agentYaml = readAgentYaml(name);
    const agentEndpoint = agentYaml?.projectEndpoint;

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: isTTY,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      agentEndpoint,
    });

    const gatewayCtx = await resolveGatewayContext({
      interactive: isTTY,
      subscriptionId,
      resourceGroup: typeof this.resourceGroup === "string" ? this.resourceGroup : undefined,
    });

    const deps = defaultRemoveAgentDeps({
      credential,
      project,
      resolveKvUri,
      gatewayCtx,
      home: undefined,
      onProgress: progress,
    });

    const plan = await planAgentRemoval(name, deps);

    // ── confirm gate ──────────────────────────────────────────────────────────
    if (!yes) {
      // Build a human-readable list of what will happen
      const items: string[] = [];
      if (!keepBindings && plan.bindings.length > 0) {
        const bSummary = plan.bindings.map((b) => `${b.channel}:${b.bindingId}`).join(", ");
        items.push(`${plan.bindings.length.toString()} binding(s) [${bSummary}]`);
      }
      if (plan.hasA2a && !keepA2a) items.push("a2a connection");
      if (plan.hasBrain) {
        const repoNote = plan.brainRepo
          ? `repo ${plan.brainRepo} KEPT — pass --delete-brain-repo to delete`
          : "brain connection";
        items.push(`brain connection (${repoNote})`);
      }
      if (plan.agentExists) items.push(`the ${plan.kind ?? "unknown"} agent`);
      items.push("local yaml");

      const itemList = items.length > 0 ? items.map((i) => `  - ${i}`).join("\n") : "  (nothing to remove)";
      this.context.stderr.write(
        `Will remove for ${colors.field(name)}:\n${itemList}\n\n`,
      );

      if (!isTTY) {
        throw new LocalCliError({
          code: "REMOVE_NO_CONFIRM",
          message: `Refusing to remove '${name}' without explicit confirmation. Pass --yes to confirm non-interactively.`,
          hint: "Pass --yes to remove non-interactively, or re-run in a TTY.",
        });
      }
      // Interactive TTY prompt deferred — mirror brain/unlink.ts pattern.
      throw new LocalCliError({
        code: "INTERACTIVE_PROMPT_NOT_IMPLEMENTED",
        message: "Interactive confirm prompt not yet implemented. Pass --yes to confirm.",
        hint: "Pass --yes to remove non-interactively.",
      });
    }

    // ── execute cascade ───────────────────────────────────────────────────────
    const opts: RemoveOpts = { keepBindings, keepA2a, deleteBrainRepo };
    const { steps } = await executeAgentRemoval(plan, opts, deps);

    // ── render ────────────────────────────────────────────────────────────────
    if (mode === "json") {
      this.context.stdout.write(renderJson({ agentName: name, steps }) + "\n");
      return steps.some((s) => s.status === "failed") ? 1 : 0;
    }

    for (const step of steps) {
      if (step.status === "ok") {
        this.context.stdout.write(`  ${colors.success("✓")} ${step.name}\n`);
      } else if (step.status === "skipped") {
        this.context.stdout.write(`  ${colors.hint("·")} ${step.name} ${colors.hint(`(${step.detail ?? "skipped"})`)}\n`);
      } else {
        this.context.stderr.write(
          `  ${colors.error("✗")} ${step.name}: ${step.detail ?? "failed"}\n`,
        );
      }
    }

    const failedSteps = steps.filter((s) => s.status === "failed");
    if (failedSteps.length > 0) {
      this.context.stderr.write(
        `\n${colors.error("✗")} ${failedSteps.length.toString()} step(s) failed. Re-run 'm8t agent remove ${name} --yes' to retry the failed steps.\n`,
      );
      return 1;
    }

    this.context.stdout.write(`\n${colors.success("✓")} removed ${colors.field(name)}.\n`);
    return 0;
  }
}
